import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, Copy, Calendar, FilmIcon, Sparkles, ChevronLeft, GraduationCap } from "lucide-react";
import { listProjects, createProject, deleteProject, duplicateProject, fetchStorylines } from "@/lib/api";

const ProjectsList = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [storylines, setStorylines] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("production");
  const [templateId, setTemplateId] = useState("default");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([listProjects(), fetchStorylines()]);
      setProjects(p);
      setStorylines(s);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const proj = await createProject({ name: name.trim(), type, template_id: templateId });
      setShowCreate(false);
      setName("");
      navigate(`/editor/${proj.id}`);
    } finally { setBusy(false); }
  };

  const onDuplicate = async (e, id) => {
    e?.stopPropagation();
    const newName = window.prompt("コピーの名前を入力", "コピー");
    if (!newName) return;
    await duplicateProject(id, newName);
    load();
  };

  const onDelete = async (e, id, name) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!window.confirm(`「${name}」を削除します。よろしいですか？`)) return;
    try {
      await deleteProject(id);
      await load();
    } catch (err) {
      alert("削除に失敗しました: " + (err?.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="min-h-screen film-grain" data-testid="projects-list-page">
      {/* Header */}
      <header className="px-8 lg:px-16 py-6 flex items-center justify-between border-b" style={{borderColor: "var(--border-subtle)"}}>
        <Link to="/" className="flex items-center gap-3" data-testid="back-to-landing">
          <ChevronLeft size={16} style={{color: "var(--gold)"}}/>
          <span className="font-display tracking-wide" style={{color: "var(--gold)"}}>FAREWELL</span>
        </Link>
        <button
          className="btn-gold"
          onClick={() => setShowCreate(true)}
          data-testid="new-project-button"
        >
          <Plus size={14}/> 新規プロジェクト
        </button>
      </header>

      <div className="px-8 lg:px-16 py-12">
        <p className="text-xs uppercase tracking-widest mb-3" style={{color: "var(--gold)"}}>Projects</p>
        <h1 className="font-display text-5xl mb-2">あなたのプロジェクト</h1>
        <p className="text-sm mb-12" style={{color: "var(--text-secondary)"}}>
          本番用と練習用、何度でも作れます。
        </p>

        {projects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="panel p-16 text-center"
            data-testid="empty-projects-state"
          >
            <FilmIcon size={48} style={{color: "var(--gold)"}} className="mx-auto mb-6 opacity-60"/>
            <h2 className="font-display text-2xl mb-3">まだプロジェクトがありません</h2>
            <p className="text-sm mb-8" style={{color: "var(--text-secondary)"}}>
              最初のプロジェクトを作成して、編集を始めましょう。
            </p>
            <button className="btn-gold" onClick={() => setShowCreate(true)} data-testid="empty-state-create-button">
              <Plus size={14}/> 新規プロジェクト
            </button>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p, i) => {
              const progress = p.total_cut_count ? Math.round((p.assigned_cut_count / p.total_cut_count) * 100) : 0;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="panel p-6 hover:border-[var(--gold)] transition-colors group cursor-pointer"
                  onClick={() => navigate(`/editor/${p.id}`)}
                  data-testid={`project-card-${p.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        {p.type === "practice" ? (
                          <span className="chip chip-blue"><GraduationCap size={10}/> 練習</span>
                        ) : (
                          <span className="chip chip-gold"><Sparkles size={10}/> 本番</span>
                        )}
                      </div>
                      <h3 className="font-display text-xl truncate" data-testid={`project-name-${p.id}`}>{p.name}</h3>
                    </div>
                  </div>
                  <div className="space-y-2 mb-4 text-xs" style={{color: "var(--text-muted)"}}>
                    <div className="flex justify-between">
                      <span>素材</span>
                      <span className="text-tc" style={{color: "var(--text-secondary)"}}>{p.material_count} 本</span>
                    </div>
                    <div className="flex justify-between">
                      <span>カット割当</span>
                      <span className="text-tc" style={{color: "var(--text-secondary)"}}>{p.assigned_cut_count} / {p.total_cut_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span><Calendar size={10} className="inline mr-1"/>更新</span>
                      <span className="text-tc">{new Date(p.updated_at).toLocaleDateString("ja-JP")}</span>
                    </div>
                  </div>
                  <div className="h-1 rounded-full overflow-hidden mb-4" style={{background: "var(--bg-deep)"}}>
                    <div className="h-full" style={{width: `${progress}%`, background: "var(--gold)", transition: "width 0.4s"}}/>
                  </div>
                  <div className="flex justify-end gap-2 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    <button className="btn-ghost !p-2" title="複製" onClick={(e) => onDuplicate(e, p.id)} data-testid={`duplicate-${p.id}`}>
                      <Copy size={12}/>
                    </button>
                    <button className="btn-danger-ghost !p-2" title="削除" onClick={(e) => onDelete(e, p.id, p.name)} data-testid={`delete-${p.id}`}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background: "rgba(6, 8, 16, 0.85)", backdropFilter: "blur(8px)"}} onClick={() => setShowCreate(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="panel-elevated p-8 max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="create-project-dialog"
          >
            <p className="text-xs uppercase tracking-widest mb-3" style={{color: "var(--gold)"}}>New project</p>
            <h2 className="font-display text-3xl mb-6">プロジェクトを作成</h2>

            <label className="block text-xs uppercase tracking-wider mb-2" style={{color: "var(--text-muted)"}}>名前</label>
            <input
              className="cinema mb-6"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例：2026年度 3年生送り出し"
              autoFocus
              data-testid="project-name-input"
            />

            <label className="block text-xs uppercase tracking-wider mb-2" style={{color: "var(--text-muted)"}}>タイプ</label>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                className={`p-4 rounded text-left text-sm transition-all ${type === "practice" ? "border-[var(--gold)] bg-[rgba(212,165,116,0.08)]" : "border-[var(--border-strong)]"}`}
                style={{border: "1px solid", color: type === "practice" ? "var(--gold)" : "var(--text-secondary)"}}
                onClick={() => setType("practice")}
                data-testid="project-type-practice"
              >
                <GraduationCap size={16} className="mb-1"/>
                <div className="font-medium mb-1">練習</div>
                <div className="text-[11px] opacity-70">操作習得用。Pexelsで素材を使う想定。</div>
              </button>
              <button
                className={`p-4 rounded text-left text-sm transition-all ${type === "production" ? "border-[var(--gold)] bg-[rgba(212,165,116,0.08)]" : "border-[var(--border-strong)]"}`}
                style={{border: "1px solid", color: type === "production" ? "var(--gold)" : "var(--text-secondary)"}}
                onClick={() => setType("production")}
                data-testid="project-type-production"
              >
                <Sparkles size={16} className="mb-1"/>
                <div className="font-medium mb-1">本番</div>
                <div className="text-[11px] opacity-70">実素材を使った本制作用。</div>
              </button>
            </div>

            <label className="block text-xs uppercase tracking-wider mb-2" style={{color: "var(--text-muted)"}}>ストーリー構成</label>
            <select className="cinema mb-8" value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="project-template-select">
              {storylines.map(s => (
                <option key={s.id} value={s.id}>{s.name} — {s.description}</option>
              ))}
            </select>

            <div className="flex gap-3 justify-end">
              <button className="btn-ghost" onClick={() => setShowCreate(false)} data-testid="cancel-create">キャンセル</button>
              <button className="btn-gold" onClick={onCreate} disabled={busy || !name.trim()} data-testid="confirm-create">
                {busy ? "作成中..." : "作成して開く"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ProjectsList;
